import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  Users,
  Briefcase,
  Briefcase as JobsIcon,
  ShieldAlert,
  Power,
  Search,
  RefreshCw,
  SlidersHorizontal,
  X as XIcon,
  Save,
  Pencil,
  KeyRound,
  CalendarCheck,
  Wand2,
  CheckCircle2,
  Infinity as InfinityIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { formatUSPhone } from "../lib/format";
import { useAuth } from "../auth/AuthContext";
import { useConfirm } from "../hooks/useConfirm";

const PLAN_LABELS = {
  owner: "Owner",
  trial: "Trial 14d",
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

export default function SuperAdminTenants() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/super-admin/tenants"),
        api.get("/super-admin/stats"),
      ]);
      setTenants(t.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function toggleStatus(t) {
    const target = t.status === "suspended" ? "active" : "suspended";
    const verb = target === "suspended" ? "suspender" : "reativar";
    const ok = await confirm({
      title: `${verb[0].toUpperCase() + verb.slice(1)} "${t.name}"?`,
      description:
        target === "suspended"
          ? "Usuários dessa empresa NÃO conseguirão mais fazer login até serem reativados. Os dados permanecem intactos."
          : "Usuários voltarão a conseguir fazer login normalmente.",
      destructive: target === "suspended",
      actionLabel: verb[0].toUpperCase() + verb.slice(1),
    });
    if (!ok) return;
    try {
      await api.patch(`/super-admin/tenants/${t.id}/status`, { status: target });
      toast.success(`"${t.name}" ${target === "suspended" ? "suspensa" : "reativada"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tenants;
    return tenants.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(term) ||
        (t.slug || "").toLowerCase().includes(term) ||
        (t.created_by_email || "").toLowerCase().includes(term),
    );
  }, [tenants, q]);

  const [editingLimits, setEditingLimits] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [fixingSchedule, setFixingSchedule] = useState(null);

  if (user && !user.is_super_admin) {
    return null; // Layout already guards this
  }

  return (
    <div className="space-y-6" data-testid="super-admin-tenants-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Empresas</h1>
          <p className="text-sm text-slate-400 mt-1">
            Gerencie todas as empresas (tenants) — suspender, reativar, e
            definir limites de plano.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={async () => {
              const testCount = tenants.filter((t) => {
                const n = (t.name || "").toLowerCase();
                const s = (t.slug || "").toLowerCase();
                const e = (t.created_by_email || t.contact_email || "").toLowerCase();
                return (
                  n.startsWith("test_") || n.startsWith("test ") ||
                  n.startsWith("demo ") || n.startsWith("demo_") ||
                  s.startsWith("test-tenant") || s.startsWith("test_tenant") ||
                  s.startsWith("demo-") || e.endsWith("@example.com")
                );
              }).length;
              if (testCount === 0) {
                toast.info("Nenhum tenant de teste ou demo encontrado");
                return;
              }
              if (!window.confirm(
                `Excluir ${testCount} tenant(s) de teste/demo? Isto remove permanentemente tenants com nome TEST_/Demo, slug test-tenant/demo-, ou e-mail @example.com (e todos os dados deles).`,
              )) return;
              try {
                const { data } = await api.post("/super-admin/tenants/cleanup-tests");
                toast.success(`${data.deleted_count} tenant(s) de teste removidos`);
                load();
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Falha");
              }
            }}
            data-testid="cleanup-tests-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-sm font-semibold text-red-300"
          >
            🧹 Limpar testes & demos
          </button>
          <button
            onClick={load}
            data-testid="refresh-tenants-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-slate-200"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
      </header>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Empresas ativas"
            value={stats.tenants.active}
            sub={`${stats.tenants.total} total · ${stats.tenants.suspended} suspensas`}
            color="emerald"
          />
          <StatCard
            label="Em trial"
            value={stats.tenants.trial}
            sub="primeiros 14 dias"
            color="amber"
          />
          <StatCard
            label="Usuários totais"
            value={stats.users_total}
            sub="todas as empresas"
            color="violet"
          />
          <StatCard
            label="Limpezas executadas"
            value={stats.jobs_total}
            sub="histórico completo"
            color="fuchsia"
          />
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome, slug ou e-mail..."
          data-testid="tenants-search"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-white/10 bg-white/5 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm text-white placeholder:text-slate-500"
        />
      </div>

      {/* Tenants table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 border-b border-white/10 bg-white/5">
          <div className="col-span-3">Empresa</div>
          <div className="col-span-2">Plano</div>
          <div className="col-span-1 text-center">
            <Users size={14} className="inline" />
          </div>
          <div className="col-span-1 text-center">
            <Briefcase size={14} className="inline" />
          </div>
          <div className="col-span-1 text-center">
            <JobsIcon size={14} className="inline" />
          </div>
          <div className="col-span-1">Criada</div>
          <div className="col-span-3 text-right">Ações</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400">Carregando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            Nenhuma empresa encontrada.
          </div>
        ) : (
          filtered.map((t) => (
            <TenantRow
              key={t.id}
              t={t}
              onToggleStatus={() => toggleStatus(t)}
              onEditLimits={() => setEditingLimits(t)}
              onEditTenant={() => setEditingTenant(t)}
              onFixSchedule={() => setFixingSchedule(t)}
            />
          ))
        )}
      </div>

      {editingLimits && (
        <LimitsModal
          tenant={editingLimits}
          onClose={() => setEditingLimits(null)}
          onSaved={() => {
            setEditingLimits(null);
            load();
          }}
        />
      )}
      {editingTenant && (
        <EditTenantModal
          tenant={editingTenant}
          onClose={() => setEditingTenant(null)}
          onSaved={() => {
            setEditingTenant(null);
            load();
          }}
        />
      )}
      {fixingSchedule && (
        <ScheduleFixModal
          tenant={fixingSchedule}
          onClose={() => setFixingSchedule(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  const palette =
    {
      emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30",
      amber: "from-amber-500/20 to-amber-500/5 border-amber-500/30",
      violet: "from-violet-500/20 to-violet-500/5 border-violet-500/30",
      fuchsia: "from-fuchsia-500/20 to-fuchsia-500/5 border-fuchsia-500/30",
      blue: "from-blue-500/20 to-blue-500/5 border-blue-500/30",
    }[color] || "from-white/5 to-white/0 border-white/10";
  return (
    <div
      className={`rounded-xl border bg-gradient-to-br ${palette} p-4`}
      data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}
    >
      <div className="text-xs uppercase tracking-wide text-slate-300/80 font-semibold">
        {label}
      </div>
      <div className="text-3xl font-bold text-white mt-1">{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}

function TenantRow({ t, onToggleStatus, onEditLimits, onEditTenant, onFixSchedule }) {
  const date = t.created_at ? new Date(t.created_at).toLocaleDateString("en-US") : "—";
  const isSuspended = t.status === "suspended";
  const statusClass =
    {
      active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
      trial: "bg-amber-500/20 text-amber-300 border-amber-500/40",
      suspended: "bg-red-500/20 text-red-300 border-red-500/40",
    }[t.status] || "bg-white/10 text-slate-300 border-white/20";
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition"
      data-testid={`tenant-row-${t.id}`}
    >
      <div className="md:col-span-3 flex items-center gap-3 min-w-0 col-span-2">
        <div className="w-10 h-10 rounded-lg bg-violet-500/20 text-violet-300 flex items-center justify-center flex-shrink-0">
          <Building2 size={18} />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-white truncate">{t.name}</div>
          <div className="text-xs text-slate-400 truncate">{t.slug}</div>
          {t.created_by_email && (
            <div className="text-xs text-slate-500 truncate">
              {t.created_by_email}
            </div>
          )}
        </div>
      </div>
      <div className="md:col-span-2 flex items-center gap-2">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${statusClass}`}>
          {t.status}
        </span>
        <span className="text-xs text-slate-300">{PLAN_LABELS[t.plan] || t.plan}</span>
      </div>
      <div className="md:col-span-1 text-center">
        <div className="md:hidden text-xs text-slate-500">Users</div>
        <div className="font-semibold text-slate-100">{t.user_count ?? "—"}</div>
      </div>
      <div className="md:col-span-1 text-center">
        <div className="md:hidden text-xs text-slate-500">Clientes</div>
        <div className="font-semibold text-slate-100">{t.client_count ?? "—"}</div>
      </div>
      <div className="md:col-span-1 text-center">
        <div className="md:hidden text-xs text-slate-500">Jobs</div>
        <div className="font-semibold text-slate-100">{t.job_count ?? "—"}</div>
      </div>
      <div className="md:col-span-1 text-xs text-slate-400 whitespace-nowrap">{date}</div>
      <div className="md:col-span-3 flex justify-end gap-2 col-span-2 flex-wrap">
        <button
          onClick={onEditTenant}
          data-testid={`edit-tenant-${t.id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-blue-500/40 bg-blue-500/15 text-blue-200 hover:bg-blue-500/25 transition"
          title="Editar dados da empresa e admin"
        >
          <Pencil size={13} /> Editar
        </button>
        <button
          onClick={onEditLimits}
          data-testid={`edit-limits-${t.id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 transition"
          title="Editar limites do plano"
        >
          <SlidersHorizontal size={13} /> Limites
        </button>
        <button
          onClick={onFixSchedule}
          data-testid={`fix-schedule-${t.id}`}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-200 hover:bg-fuchsia-500/25 transition"
          title="Corrigir agenda desta empresa"
        >
          <CalendarCheck size={13} /> Corrigir agenda
        </button>
        <button
          onClick={onToggleStatus}
          data-testid={`toggle-status-${t.id}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${
            isSuspended
              ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
              : "border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25"
          }`}
          title={isSuspended ? "Reativar empresa" : "Suspender empresa"}
        >
          <Power size={13} /> {isSuspended ? "Reativar" : "Suspender"}
        </button>
      </div>
    </div>
  );
}

function LimitsModal({ tenant, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    max_collaborators: "",
    max_clients: "",
    max_teams: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/super-admin/tenants/${tenant.id}/limits`);
        setData(data);
        setForm({
          max_collaborators:
            data.explicit?.max_collaborators ?? "",
          max_clients: data.explicit?.max_clients ?? "",
          max_teams: data.explicit?.max_teams ?? "",
        });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Falha");
      }
    })();
  }, [tenant.id]);

  async function save() {
    setSaving(true);
    try {
      const body = {
        max_collaborators:
          form.max_collaborators === "" ? null : Number(form.max_collaborators),
        max_clients: form.max_clients === "" ? null : Number(form.max_clients),
        max_teams: form.max_teams === "" ? null : Number(form.max_teams),
      };
      await api.patch(`/super-admin/tenants/${tenant.id}/limits`, body);
      toast.success("Limites atualizados");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full text-white"
        data-testid="limits-modal"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-violet-300 font-semibold">
              Limites do plano
            </div>
            <h2 className="text-lg font-bold text-white">{tenant.name}</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Plano atual: <span className="font-semibold">{tenant.plan}</span> ·
              Use <b>0</b> para ilimitado · Deixe <b>vazio</b> para usar o
              default do plano.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"
            data-testid="limits-close"
          >
            <XIcon size={18} />
          </button>
        </div>

        {!data ? (
          <div className="p-10 text-center text-slate-400">Carregando…</div>
        ) : (
          <div className="p-5 space-y-4">
            <LimitField
              label="Máximo de colaboradores"
              field="max_collaborators"
              form={form}
              setForm={setForm}
              usage={data.usage.collaborators}
              effective={data.limits.max_collaborators}
              defaultVal={data.plan_defaults[data.plan]?.max_collaborators}
              testid="limit-collaborators"
            />
            <LimitField
              label="Máximo de clientes"
              field="max_clients"
              form={form}
              setForm={setForm}
              usage={data.usage.clients}
              effective={data.limits.max_clients}
              defaultVal={data.plan_defaults[data.plan]?.max_clients}
              testid="limit-clients"
            />
            <LimitField
              label="Máximo de equipes"
              field="max_teams"
              form={form}
              setForm={setForm}
              usage={data.usage.teams}
              effective={data.limits.max_teams}
              defaultVal={data.plan_defaults[data.plan]?.max_teams}
              testid="limit-teams"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving || !data}
            data-testid="limits-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function LimitField({ label, field, form, setForm, usage, effective, defaultVal, testid }) {
  const used = usage ?? 0;
  const effLabel = effective === 0 ? "ilimitado" : effective;
  const overLimit = effective > 0 && used > effective;
  const atLimit = effective > 0 && used >= effective;
  const pct = effective > 0 ? Math.min(100, (used / effective) * 100) : 0;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-white">{label}</span>
        <span
          className={`text-xs font-mono ${
            overLimit ? "text-red-300" : atLimit ? "text-amber-300" : "text-slate-400"
          }`}
        >
          {used} / {effLabel} usado
        </span>
      </div>
      {effective > 0 && (
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className={`h-full transition-all ${
              overLimit
                ? "bg-red-500"
                : pct >= 80
                  ? "bg-amber-500"
                  : "bg-emerald-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          value={form[field]}
          onChange={(e) =>
            setForm((f) => ({ ...f, [field]: e.target.value }))
          }
          placeholder={`Default do plano: ${defaultVal === 0 ? "∞" : defaultVal ?? "—"}`}
          className="flex-1 rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-white"
        />
        {form[field] === "" && (
          <span className="text-xs text-slate-500 italic flex items-center gap-1">
            usa default
          </span>
        )}
        {String(form[field]) === "0" && (
          <span className="text-xs text-emerald-400 flex items-center gap-1">
            <InfinityIcon size={14} /> ilimitado
          </span>
        )}
      </div>
    </div>
  );
}

function EditTenantModal({ tenant, onClose, onSaved }) {
  const [tForm, setTForm] = useState({
    name: tenant.name || "",
    plan: tenant.plan || "trial",
    contact_email: tenant.contact_email || "",
    contact_phone: tenant.contact_phone || "",
  });
  const [admin, setAdmin] = useState(null);
  const [aForm, setAForm] = useState({ name: "", email: "", phone: "", role: "admin", status: "ativo" });
  const [savingT, setSavingT] = useState(false);
  const [savingA, setSavingA] = useState(false);
  const [resetVal, setResetVal] = useState("");
  const [linkInfo, setLinkInfo] = useState(null);
  const [generatedPwd, setGeneratedPwd] = useState(null);

  function generateRandom() {
    // Easy-to-share password: 3 words + 2 digits
    const words = ["Sol", "Mar", "Rio", "Lua", "Flor", "Onda", "Folha", "Vento"];
    const w = () => words[Math.floor(Math.random() * words.length)];
    const n = () => Math.floor(Math.random() * 100).toString().padStart(2, "0");
    const pwd = `${w()}${w()}${n()}`;
    setResetVal(pwd);
    setGeneratedPwd(null);
  }

  useEffect(() => {
    // Busca TODOS os usuários do tenant (sem filtro de role) e prioriza
    // admin > supervisor > owner > qualquer outro. Garante que o modal
    // funcione mesmo para tenants criados sem role exato "admin".
    const ROLE_RANK = { admin: 0, owner: 1, supervisor: 2 };
    api.get(`/super-admin/users?tenant_id=${tenant.id}`).then(({ data }) => {
      const list = data || [];
      const sorted = [...list].sort(
        (a, b) =>
          (ROLE_RANK[a.role] ?? 99) - (ROLE_RANK[b.role] ?? 99),
      );
      const found = sorted[0] || null;
      setAdmin(found);
      if (found) {
        setAForm({
          name: found.name || "",
          email: found.email || "",
          phone: found.phone || "",
          role: found.role || "admin",
          status: found.status || "ativo",
        });
        // Auto-preenche contato da empresa se vazio com dados do admin
        setTForm((prev) => ({
          ...prev,
          contact_email: prev.contact_email || found.email || "",
          contact_phone: prev.contact_phone || found.phone || "",
        }));
      }
    }).catch(() => {});
  }, [tenant.id]);

  async function saveTenant() {
    setSavingT(true);
    try {
      await api.patch(`/super-admin/tenants/${tenant.id}`, tForm);
      toast.success("Empresa atualizada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSavingT(false);
    }
  }

  async function saveAdmin() {
    if (!admin) return;
    setSavingA(true);
    try {
      await api.patch(`/super-admin/users/${admin.id}`, aForm);
      toast.success("Admin atualizado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSavingA(false);
    }
  }

  async function forceReset() {
    if (!admin || !resetVal || resetVal.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres");
      return;
    }
    try {
      const { data } = await api.post(`/super-admin/users/${admin.id}/force-reset`, { new_password: resetVal });
      setGeneratedPwd(data.new_password);
      setResetVal("");
      toast.success("Senha atualizada com sucesso");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  async function copyPwd() {
    try {
      await navigator.clipboard.writeText(generatedPwd);
      toast.success("Senha copiada");
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  function shareViaWhatsApp() {
    if (!generatedPwd) return;
    const phone = (admin.phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(
      `Olá ${admin.name?.split(" ")[0] || ""}, sua nova senha temporária do CleanManager é: ${generatedPwd}\n\nFaça login em https://cleanmanagerusa.com e troque por uma senha pessoal.`,
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  }

  async function sendResetLink() {
    if (!admin) return;
    try {
      const { data } = await api.post(`/super-admin/users/${admin.id}/reset-link`);
      setLinkInfo(data);
      toast.success("Link de reset gerado (válido 24h)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="edit-tenant-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="font-bold text-white">Editar empresa & admin</div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:bg-white/10"><XIcon size={18} /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Tenant fields */}
          <section>
            <div className="text-xs uppercase tracking-widest text-violet-300 font-bold mb-2">
              Dados da empresa
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nome">
                <Inp v={tForm.name} on={(v) => setTForm({ ...tForm, name: v })} tid="et-name" />
              </Field>
              <Field label="Plano">
                <select
                  className="cm-input bg-slate-950/60 border-white/10 text-white"
                  value={tForm.plan}
                  onChange={(e) => setTForm({ ...tForm, plan: e.target.value })}
                  data-testid="et-plan"
                >
                  {["trial", "free", "starter", "pro", "enterprise", "owner"].map((p) => (
                    <option key={p} value={p}>{PLAN_LABELS[p] || p}</option>
                  ))}
                </select>
              </Field>
              <Field label="E-mail de contato">
                <Inp v={tForm.contact_email} on={(v) => setTForm({ ...tForm, contact_email: v })} tid="et-email" />
              </Field>
              <Field label="Telefone de contato">
                <Inp v={tForm.contact_phone} on={(v) => setTForm({ ...tForm, contact_phone: v })} tid="et-phone" mask="us-phone" />
              </Field>
            </div>
            <button
              onClick={saveTenant}
              disabled={savingT}
              data-testid="save-tenant"
              className="mt-3 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold"
            >
              {savingT ? "Salvando…" : "Salvar empresa"}
            </button>
          </section>

          {/* Admin fields */}
          <section className="border-t border-white/10 pt-5">
            <div className="text-xs uppercase tracking-widest text-blue-300 font-bold mb-2">
              Admin principal {admin ? "" : "(nenhum admin encontrado)"}
            </div>
            {admin && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Nome">
                    <Inp v={aForm.name} on={(v) => setAForm({ ...aForm, name: v })} tid="ea-name" />
                  </Field>
                  <Field label="E-mail (login)">
                    <Inp v={aForm.email} on={(v) => setAForm({ ...aForm, email: v })} tid="ea-email" />
                  </Field>
                  <Field label="Telefone">
                    <Inp v={aForm.phone} on={(v) => setAForm({ ...aForm, phone: v })} tid="ea-phone" mask="us-phone" />
                  </Field>
                  <Field label="Status">
                    <select
                      className="cm-input bg-slate-950/60 border-white/10 text-white"
                      value={aForm.status}
                      onChange={(e) => setAForm({ ...aForm, status: e.target.value })}
                      data-testid="ea-status"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </Field>
                </div>
                <button
                  onClick={saveAdmin}
                  disabled={savingA}
                  data-testid="save-admin"
                  className="mt-3 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold"
                >
                  {savingA ? "Salvando…" : "Salvar admin"}
                </button>

                {/* Password section */}
                <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-3">
                  <div className="text-xs uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1.5">
                    <KeyRound size={13} /> Resetar senha
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="text"
                      value={resetVal}
                      onChange={(e) => setResetVal(e.target.value)}
                      placeholder="Nova senha (mín. 6 caracteres)"
                      data-testid="reset-password-input"
                      className="cm-input bg-slate-950/60 border-white/10 text-white flex-1 min-w-[200px]"
                    />
                    <button
                      onClick={forceReset}
                      data-testid="reset-password-force"
                      className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold"
                    >
                      Definir senha
                    </button>
                    <button
                      onClick={sendResetLink}
                      data-testid="reset-password-link"
                      className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold"
                    >
                      Gerar link 24h
                    </button>
                  </div>
                  {linkInfo && (
                    <div className="bg-slate-950/60 border border-white/10 rounded p-2 text-xs text-slate-200 font-mono break-all" data-testid="reset-link-output">
                      {linkInfo.link}
                      <div className="text-[10px] text-slate-500 mt-1 font-sans">
                        Compartilhe via WhatsApp/Email. Válido 24h.
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-amber-200/70 leading-snug">
                    A senha nunca é mostrada — é guardada com bcrypt.
                    O Super Admin pode definir uma nova OU gerar um link único pra o usuário criar a dele.
                  </p>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Inp({ v, on, tid, mask, type, inputMode, maxLength, placeholder }) {
  const handle = (e) => {
    const raw = e.target.value;
    on(mask === "us-phone" ? formatUSPhone(raw) : raw);
  };
  return (
    <input
      value={v || ""}
      onChange={handle}
      data-testid={tid}
      type={type || (mask === "us-phone" ? "tel" : "text")}
      inputMode={inputMode || (mask === "us-phone" ? "tel" : undefined)}
      maxLength={maxLength || (mask === "us-phone" ? 14 : undefined)}
      placeholder={placeholder || (mask === "us-phone" ? "(555) 123-4567" : undefined)}
      className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-400"
    />
  );
}


const _WD = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
function _fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.replace(" ", "T"));
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${_WD[(d.getDay() + 6) % 7]} ${dd}/${mm}`;
}

function ScheduleFixModal({ tenant, onClose }) {
  const [data, setData] = useState({ clients: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const unchecked = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/schedule-audit", {
        params: { tenant_id: tenant.id, only_divergent: true },
      });
      setData(data);
      setSelected(new Set(data.clients.flatMap((c) => c.jobs.filter((j) => j.duplicate && !unchecked.current.has(j.job_id)).map((j) => j.job_id))));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);
  useEffect(() => { load(); }, [load]);

  const allIds = data.clients.flatMap((c) => c.jobs.filter((j) => (j.day_wrong || j.time_wrong) && !j.duplicate).map((j) => j.job_id));

  function toggleSel(id) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); unchecked.current.add(id); } else { n.add(id); unchecked.current.delete(id); }
      return n;
    });
  }

  async function removeSelected() {
    const ids = [...selected];
    if (!ids.length || !window.confirm(`Apagar ${ids.length} casa(s) duplicada(s) do sistema?`)) return;
    setApplying(true);
    try {
      const { data: r } = await api.post("/super-admin/schedule-audit/remove", { job_ids: ids, tenant_id: tenant.id });
      toast.success(`${r.deleted} casa(s) apagada(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao apagar");
    } finally {
      setApplying(false);
    }
  }

  async function apply(ids) {
    if (!ids.length) return;
    setApplying(true);
    try {
      const { data: r } = await api.post("/super-admin/schedule-audit/apply", { job_ids: ids, tenant_id: tenant.id });
      toast.success(`${r.updated} casa(s) corrigida(s)${r.skipped ? ` · ${r.skipped} ignorada(s)` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao corrigir");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-2xl w-full text-white max-h-[90vh] flex flex-col" data-testid="schedule-fix-modal">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-semibold">Corrigir agenda</div>
            <h2 className="text-lg font-bold">{tenant.name}</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {data.totals?.divergent_jobs ?? 0} casa(s) fora do dia/hora · {data.totals?.duplicate_jobs ?? 0} duplicada(s).
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"><XIcon size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {loading ? (
            <div className="text-center text-slate-400 py-10">Carregando…</div>
          ) : data.clients.length === 0 ? (
            <div className="text-center text-slate-300 py-10" data-testid="fix-empty">
              <CheckCircle2 size={36} className="mx-auto mb-2 text-emerald-400" />
              Agenda alinhada! Nenhuma casa fora do dia/hora.
            </div>
          ) : (
            data.clients.map((c) => {
              const ids = c.jobs.filter((j) => (j.day_wrong || j.time_wrong) && !j.duplicate).map((j) => j.job_id);
              return (
                <div key={c.client_id} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white/5">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.client_name}</div>
                      <div className="text-[11px] text-slate-400">
                        {c.has_config
                          ? `Cadastrado: ${(c.target_weekdays || []).map((w) => _WD[w]).join(", ")} às ${c.target_time}`
                          : "⚠ Sem dia/hora cadastrado"}
                      </div>
                    </div>
                    {ids.length > 0 && (
                      <button onClick={() => apply(ids)} disabled={applying} data-testid={`fix-client-${c.client_id}`}
                        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-200 hover:bg-fuchsia-500/25">
                        <Wand2 size={13} /> Corrigir {ids.length}
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-white/5">
                    {c.jobs.map((j) => (
                      <div key={j.job_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`fix-job-${j.job_id}`}>
                        <input type="checkbox" checked={selected.has(j.job_id)} onChange={() => toggleSel(j.job_id)}
                          className="w-4 h-4 accent-red-500" title="Marcar para apagar" data-testid={`fix-select-${j.job_id}`} />
                        <span className="text-red-300 font-medium">{_fmtDate(j.current)} · {j.current_time}</span>
                        {j.duplicate ? (
                          <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-200 font-semibold text-xs" data-testid={`fix-dup-${j.job_id}`}>
                            Duplicada de {_fmtDate(j.duplicate_of)}
                          </span>
                        ) : (
                          <>
                            <span className="text-slate-500">→</span>
                            <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200 font-semibold">{_fmtDate(j.proposed)} · {j.proposed_time}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">Fechar</button>
          {selected.size > 0 && (
            <button onClick={removeSelected} disabled={applying} data-testid="fix-remove-selected"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-semibold disabled:opacity-50">
              Apagar selecionadas ({selected.size})
            </button>
          )}
          {allIds.length > 0 && (
            <button onClick={() => apply(allIds)} disabled={applying} data-testid="fix-all"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-sm font-semibold disabled:opacity-50">
              {applying ? <RefreshCw size={14} className="animate-spin" /> : <Wand2 size={14} />} Corrigir todas ({allIds.length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
